"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { Block } from "@/types/block";
import {
    createFormulaRuntime,
    buildFormulaDefinitionsFromSchema,
    FormulaNoteLike
} from "@/lib/formula/evaluator";
import { createBoardRelationResolver } from "@/lib/formula/relationResolver";
import { computeRollupData, getRollupComparableValue } from "@/utils/rollupUtils";
import { DatabaseSource, BoardProperties } from "@/types/board";

/**
 * Hook to recompute rollups and formulas for a set of notes on-the-fly.
 * This follows the approach of derived state rather than persisting results.
 *
 * When `refreshNotes` is provided, a formula referencing an unhydrated linked
 * datasource fetches it on demand and re-evaluates once it arrives.
 */
export function useComputedNotes(
    notes: Block[],
    properties: Record<string, any>,
    boardId: string,
    getNotesByDataSourceId: (dataSourceId: string) => Block[],
    getDataSource: (dataSourceId: string) => DatabaseSource | undefined,
    refreshNotes?: (dataSourceId: string) => Promise<void>
): Block[] {
    // Bumped when an on-demand fetch lands, to re-run the memo (the context
    // getters are ref-stable and won't trigger it on their own).
    const [loadVersion, setLoadVersion] = useState(0);
    // Datasource ids already requested, so each is fetched at most once.
    const requestedRef = useRef<Set<string>>(new Set());

    // missingKey is a stable string of the datasources this pass couldn't
    // resolve, so the fetch effect only re-runs when that set changes.
    const { computed, missingKey } = useMemo(() => {
        const missing = new Set<string>();
        if (!notes || notes.length === 0 || !properties || Object.keys(properties).length === 0) {
            return { computed: notes, missingKey: "" };
        }

        // 1. Identify formula and rollup properties
        const propertyEntries = Object.entries(properties);
        const rollupProperties = propertyEntries.filter(([_, p]) => p.type === "rollup");
        const hasRollups = rollupProperties.length > 0;

        const definitions = buildFormulaDefinitionsFromSchema(properties);
        const runtime = createFormulaRuntime(definitions, {
            // Lets formulas traverse relation properties (relatedCount /
            // countIf / sumBy / …) over datasources the board context has
            // hydrated — the same reach rollups already use.
            resolveRelation: createBoardRelationResolver(
                properties,
                getNotesByDataSourceId,
                getDataSource,
                refreshNotes
                    ? {
                          onMissing: (dsId) => missing.add(dsId),
                          isFetched: (dsId) => requestedRef.current.has(dsId),
                      }
                    : undefined
            ),
        });
        
        const hasFormulas = runtime.hasFormulas;

        // If no rollups or formulas exist, just return original notes
        if (!hasRollups && !hasFormulas) {
            return { computed: notes, missingKey: "" };
        }

        // 2. Process each note
        const computed = notes.map((originalNote) => {
            // Start with a clone of the note's properties
            const computedProperties = { ...(originalNote.value.databaseProperties || {}) };
            const formulaErrors = { ...(originalNote.value.formulaErrors || {}) };

            // 3. Compute Rollups first
            if (hasRollups) {
                rollupProperties.forEach(([id, schema]) => {
                    const rollupResult = computeRollupData(
                        originalNote,
                        schema,
                        properties as BoardProperties,
                        getNotesByDataSourceId,
                        getDataSource
                    );

                    const comparableValue = getRollupComparableValue(rollupResult);
                    if (comparableValue !== null) {
                        computedProperties[id] = comparableValue;
                    }
                });
            }

            // 4. Compute Formulas second (so they can use computed rollups)
            if (hasFormulas) {
                const noteLike: FormulaNoteLike = {
                    _id: originalNote._id,
                    title: originalNote.value.title,
                    databaseProperties: computedProperties,
                    formulaErrors: formulaErrors,
                    // Row stamps, so prop("Created time") and friends resolve.
                    createdAt: originalNote.createdAt,
                    createdBy: originalNote.createdBy,
                    updatedAt: originalNote.updatedAt,
                    lastEditedBy: originalNote.lastEditedBy,
                };

                const formulaResults = runtime.recomputeFormulasForNote(noteLike);

                // Merge formula results into computedProperties
                Object.assign(computedProperties, formulaResults.note.databaseProperties);
                Object.assign(formulaErrors, formulaResults.note.formulaErrors);
            }

            return {
                ...originalNote,
                value: {
                    ...originalNote.value,
                    databaseProperties: computedProperties,
                    formulaErrors: formulaErrors,
                }
            };
        });
        return { computed, missingKey: [...missing].sort().join("|") };
    }, [notes, properties, boardId, getNotesByDataSourceId, getDataSource, refreshNotes, loadVersion]);

    // Fetch the unresolved datasources (once each), then bump loadVersion to
    // re-run against the hydrated data. Runs only when missingKey changes.
    useEffect(() => {
        if (!refreshNotes || !missingKey) return;
        const toFetch = missingKey
            .split("|")
            .filter((id) => !requestedRef.current.has(id));
        if (toFetch.length === 0) return;
        for (const id of toFetch) requestedRef.current.add(id);

        let cancelled = false;
        Promise.allSettled(toFetch.map((id) => refreshNotes(id))).then(() => {
            if (!cancelled) setLoadVersion((v) => v + 1);
        });
        return () => {
            cancelled = true;
        };
    }, [missingKey, refreshNotes]);

    return computed;
}
